import {
  PaymentFrequenciesType,
  SelectOption,
} from 'QuoteApp/services/endpoints/GetConfig';
import { PaymentPlanType } from 'QuoteApp/services/endpoints/GetPaymentDetails';
import { FlexiblePaymentFrequency } from 'QuoteApp/services/endpoints/GetPaymentDetails';
import { RateType } from 'QuoteApp/services/endpoints/GetRate';
import {
  findPaymentScheduleEntryByType,
  getSelectedPaymentPlan,
  getPaymentPlanTotalCost,
} from 'QuoteApp/utils/paymentPlan';
import { MONTHS, WEEK_DAYS, WeekDayType } from 'Shared/constants/date';
import { formatText, toCamelCase } from 'Shared/utils';

import { PaymentConcept, PaymentOptionSummary } from '../PlanComparison/types';

export const getNextDayWithSelectedDay = (
  selectedDay: string,
  effectiveDate: string,
  instance: number = 1
): string | null => {
  const effectiveDateObj = new Date(effectiveDate);
  const selectedDayIndex = WEEK_DAYS.indexOf(selectedDay as WeekDayType);

  if (selectedDayIndex === -1) {
    return null;
  }

  const effectiveDayIndex = effectiveDateObj.getUTCDay();
  const daysUntilNextSelectedDay =
    (selectedDayIndex - effectiveDayIndex + 7) % 7 || 7;

  effectiveDateObj.setUTCDate(
    effectiveDateObj.getUTCDate() +
      daysUntilNextSelectedDay +
      (instance - 1) * 7
  );

  const dayOfWeek = WEEK_DAYS[effectiveDateObj.getUTCDay()];
  const month = MONTHS[effectiveDateObj.getUTCMonth()];
  const dayOfMonth = effectiveDateObj.getUTCDate();

  return `${dayOfWeek}, ${month} ${dayOfMonth}`;
};

export const getConfigByFrequency = (
  frequency: FlexiblePaymentFrequency,
  paymentFrequencies: PaymentFrequenciesType = []
) => {
  const frequencyConfig = paymentFrequencies.find(
    (configItem) => configItem.name === frequency
  );
  if (!frequencyConfig) return [];
  return frequencyConfig.options;
};

export const buildWeekdaySelectorData = (
  paymentFrequencies?: PaymentFrequenciesType
): Array<SelectOption<string>> => {
  return getConfigByFrequency('weekly', paymentFrequencies) as Array<
    SelectOption<string>
  >;
};

export const buildBiweeklyDateSelectorData = (
  dayOfWeek?: string,
  startDate?: string
): Array<SelectOption<string>> => {
  if (!dayOfWeek) return [];

  const capitalizedSelectedDay = formatText(dayOfWeek, 'capitalize');

  return Array.from({ length: 2 }, (_, index) => {
    const instance = index + 1;
    return {
      value: `${dayOfWeek}_${instance}`,
      label:
        getNextDayWithSelectedDay(
          capitalizedSelectedDay,
          startDate || '',
          instance
        ) ?? '',
    };
  });
};

export const buildSemiMonthlySelectorData = (
  paymentFrequencies?: PaymentFrequenciesType
): Array<SelectOption<string>> => {
  const semiMonthlyConfig = getConfigByFrequency(
    'semiMonthly',
    paymentFrequencies
  ) as Array<SelectOption<Array<number>>>;

  return semiMonthlyConfig.map((option) => {
    const [day1, day2] = option.value;
    const value = `${day1},${day2}` as const;

    return {
      label: option.label,
      value,
    };
  });
};

export const buildMonthlySelectorData = (
  paymentFrequencies?: PaymentFrequenciesType
): Array<SelectOption<string>> => {
  const monthlyConfig = getConfigByFrequency(
    'monthly',
    paymentFrequencies
  ) as Array<SelectOption<number>>;

  return monthlyConfig.map((option) => {
    return {
      label: option.label,
      value: option.value.toString(),
    };
  });
};

export const buildDownpaymentConcept = (
  paymentPlan: PaymentPlanType
): PaymentConcept => {
  const downpaymentEntry = findPaymentScheduleEntryByType(
    paymentPlan.paymentSchedule,
    'DOWNPAYMENT'
  );

  return {
    type: 'amount',
    name: 'downpayment',
    label: 'DOWN PAYMENT DUE',
    value: downpaymentEntry?.amount ?? '',
  };
};

export const buildInstallmentConcept = (
  paymentPlan: PaymentPlanType
): PaymentConcept => {
  // Reading the first installment entry is enough since all installments share the same amount.
  const installmentEntry = findPaymentScheduleEntryByType(
    paymentPlan.paymentSchedule,
    'INSTALLMENT'
  );

  const installmentAmount = installmentEntry?.amount ?? '';

  if (!installmentAmount) {
    return {
      type: 'text',
      name: 'installment',
      label: 'FUTURE PAYMENTS',
      value: 'None',
    };
  }

  return {
    type: 'amount',
    name: 'installment',
    label: 'FUTURE PAYMENTS',
    value: installmentAmount,
  };
};

export const buildTotalConcept = (
  paymentPlan: PaymentPlanType
): PaymentConcept => {
  const totalCost = getPaymentPlanTotalCost(paymentPlan);

  return {
    type: 'amount',
    name: 'total',
    label: 'TOTAL COST',
    value: totalCost ?? '',
  };
};

export const buildPaymentPlanBreakdown = (paymentPlan: PaymentPlanType) => {
  return [
    buildDownpaymentConcept(paymentPlan),
    buildInstallmentConcept(paymentPlan),
    buildTotalConcept(paymentPlan),
  ];
};

const DISPLAY_NAME_BY_FREQ: Partial<Record<FlexiblePaymentFrequency, string>> =
  {
    biWeekly: 'Every two weeks', // backend returns "2 weeks" due to a Consumer requirement
    fullPay: 'Pay in full',
  };

export const getPlanDisplayName = (paymentPlan: PaymentPlanType): string => {
  const frequency = toCamelCase<FlexiblePaymentFrequency>(
    paymentPlan.paymentFrequencyIdentifier
  );

  return formatText(
    DISPLAY_NAME_BY_FREQ[frequency] || paymentPlan.paymentFrequencyLabel,
    'capitalize'
  );
};

export const mapPaymentPlanToOption = (
  paymentPlan: PaymentPlanType
): PaymentOptionSummary => {
  const frequency = toCamelCase<FlexiblePaymentFrequency>(
    paymentPlan.paymentFrequencyIdentifier
  );

  const displayName = getPlanDisplayName(paymentPlan);

  return {
    planId: paymentPlan.identifier,
    name: frequency,
    displayName,
    breakdown: buildPaymentPlanBreakdown(paymentPlan),
  };
};

// Florida (FL) is the only state with two entries for the selected payment plan.
// This occurs due to the Predictable Fee strategy applied exclusively in FL.
// We need to keep only the entry that matches the selected payment plan identifier.
export const sanitizePaymentPlanOptions = (
  paymentPlanOptions: Array<PaymentOptionSummary>,
  selectedPaymentPlan: PaymentPlanType
): Array<PaymentOptionSummary> => {
  const selectedPlanIdentifier = selectedPaymentPlan.identifier;

  // Keep the option that matches the selected payment plan identifier.
  // This logic ensures we keep the correct option when there are duplicates for the same frequency.
  const filteredOptions = paymentPlanOptions.reduce((map, option) => {
    if (!map.has(option.name)) {
      map.set(option.name, option);
    } else if (option.planId === selectedPlanIdentifier) {
      map.set(option.name, option);
    }

    return map;
  }, new Map<FlexiblePaymentFrequency, PaymentOptionSummary>());

  return Array.from(filteredOptions.values());
};

const PAYMENT_FREQUENCY_ORDER: FlexiblePaymentFrequency[] = [
  'weekly',
  'biWeekly',
  'semiMonthly',
  'monthly',
  'fullPay',
];

const PAYMENT_FREQUENCY_RANK = new Map<FlexiblePaymentFrequency, number>(
  PAYMENT_FREQUENCY_ORDER.map((frequency, index) => [frequency, index])
);

export const sortPaymentPlanOptionsByFrequency = (
  options: Array<PaymentOptionSummary>
): Array<PaymentOptionSummary> => {
  return [...options].sort((leftOption, rightOption) => {
    // If an option has an unknown frequency, assign the maximum rank so it appears at the end.
    const leftRank =
      PAYMENT_FREQUENCY_RANK.get(leftOption.name) ?? Number.MAX_SAFE_INTEGER; // Unknown -> end

    const rightRank =
      PAYMENT_FREQUENCY_RANK.get(rightOption.name) ?? Number.MAX_SAFE_INTEGER; // Unknown -> end

    return leftRank - rightRank;
  });
};

export const buildPaymentPlanFrequencyOptions = (
  rate: RateType
): Array<SelectOption<FlexiblePaymentFrequency>> => {
  const selectedPaymentPlan = getSelectedPaymentPlan(rate);
  const paymentPlans = rate.paymentDetails.attributes.paymentPlans;

  return sortPaymentPlanOptionsByFrequency(
    sanitizePaymentPlanOptions(
      paymentPlans.map(mapPaymentPlanToOption),
      selectedPaymentPlan
    )
  ).map((option) => ({
    value: option.name,
    label: option.displayName,
  }));
};
